<template>
  <div class="flex flex-col">
    <div class="grow flex flex-col justify-center items-center px-4 py-8">
      <div class="mb-2">
        <Icon icon="ok"></Icon>
      </div>
      <!--      TODO: check if rescheduling -->
      <template v-if="FMStore.showCancelMsg">
        <h1 class="text-[32px] text-darkGrey font-bold mb-2 leading-none">Done</h1>
        <div class="text-base leading-[140%] text-darkGrey font-medium mb-2 text-center">
          Your appointment to exchange the old meter has been canceled. Please book a new one when you’re ready
        </div>
      </template>
      <!--      TODO: check if scheduling -->
      <template v-else>
        <h1 class="text-[32px] text-darkGrey font-bold mb-2 leading-none">All set!</h1>
        <div class="text-base leading-[140%] text-darkGrey font-medium mb-2 text-center">
          Get ready! A technician will be at your place on <br>
          {{ format(FMStore.selectedDate, "full") }}, between
          {{ getSlotTimeRangeById(FMStore.selectedTimeSlot) }}.
        </div>
        <div class="calendar__button-wrapper">
          <button @click="isModalOpen = true"
                  class="px-2 py-[15px] text-base text-bluePrimary font-bold hover:text-[#1D73C3]">
            Add to calendar
          </button>
        </div>
      </template>
    </div>

    <!--      TODO: check if scheduling -->
    <template v-if="!FMStore.showCancelMsg">
      <div class="px-4 mb-[32px] text-sm font-medium text-grey-500 text-center">
        Please clear the space in front of your meter to allow the technician to
        do an excellent job
      </div>
    </template>
    <Buttons>
      <Button class="btn btn__primary" @click="reloadPage">
        Close
      </Button>
    </Buttons>
  </div>

  <Modal :is-open="isModalOpen"
         title="Add to calendar"
         @close="isModalOpen = false">
    <div class="flex flex-col gap-4">
      <Button class="btn btn__primary" @click="addToGoogleCalendar">
        Google Calendar
      </Button>
      <Button class="btn btn__primary" @click="addToAppleCalendar">
        Apple Calendar
      </Button>
      <Button class="btn btn__primary" @click="addToOutlookCalendar">
        Outlook
      </Button>
    </div>
  </Modal>
</template>

<script setup>
import {format} from "@formkit/tempo";
import {useFMStore} from "@/store/FMStore";
import Button from "~/components/ui/Button.vue";
import Buttons from "~/components/ui/Buttons.vue";
import Icon from "~/components/ui/Icon.vue";
import Modal from "~/components/ui/Modal.vue";

const isModalOpen = ref(false);
const FMStore = useFMStore();
const config = useRuntimeConfig();
const reminderTimezone = computed(() => config.public?.reminderTimezone || 'America/Denver');

// Reactive variables for the calendar
const calLocation = computed(() => FMStore.selectedWorkOrder?.PremiseAddress || '');
const calDate = computed(() => FMStore.selectedDate);
const calStartTime = computed(() => getStartTimeById(FMStore.selectedTimeSlot));
const calEndTime = computed(() => getEndTimeById(FMStore.selectedTimeSlot));
defineEmits(['start'])
defineProps({
  isLoading: {
    type: Boolean,
    default: false
  }
})

// Functions for working with time
function getStartTimeById(slotId) {
  const slot = FMStore.openTimeSlots.find((slot) => String(slot.TimeSlotId) === String(slotId) && String(slot.TimeSlotStart).split('T')[0] === FMStore.selectedDate);
  if (!slot) return null;
  return formatUTCTimeOnly24(slot.TimeSlotStart);
}

function getEndTimeById(slotId) {
  const slot = FMStore.openTimeSlots.find((slot) => String(slot.TimeSlotId) === String(slotId) && String(slot.TimeSlotStart).split('T')[0] === FMStore.selectedDate);
  if (!slot) return null;
  return formatUTCTimeOnly24(slot.TimeSlotEnd);
}

function getSlotTimeRangeById(slotId) {
  const slot = FMStore.openTimeSlots.find((slot) => String(slot.TimeSlotId) === String(slotId) && String(slot.TimeSlotStart).split('T')[0] === FMStore.selectedDate);
  if (!slot) return null;
  return (
      formatUTCTimeOnly(slot.TimeSlotStart) +
      " and " +
      formatUTCTimeOnly(slot.TimeSlotEnd)
  );
}

function formatUTCTimeOnly(timestamp) {
  const date = new Date(timestamp);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes} ${period}`;
}

function formatUTCTimeOnly24(timestamp) {
  const date = new Date(timestamp);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}`;
}

function createRescheduleLink() {
  const workOrderId = FMStore.selectedWorkOrder.Id;
  return `${window.location.href}appointment/${workOrderId}`;

}
// Functions for creating calendar URLs
function createGoogleCalendarUrl() {
  const title = encodeURIComponent("Water Meter Upgrade");
  const startDateTime = `${calDate.value}T${calStartTime.value}:00`;
  const endDateTime = `${calDate.value}T${calEndTime.value}:00`;
  const location = encodeURIComponent(calLocation.value);
  const details = encodeURIComponent(`Water meter replacement appointment. Please clear the space in front of your meter. <a href="${createRescheduleLink()}" target="_blank">reschedule</a>`);
  const ctz = encodeURIComponent(reminderTimezone.value);

  // We intentionally do not append Z because DB time is treated as local slot time.
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateTime.replace(/[-:]/g, '')}/${endDateTime.replace(/[-:]/g, '')}&ctz=${ctz}&location=${location}&details=${details}`;
}

function createOutlookCalendarUrl() {
  const title = encodeURIComponent("Water Meter Upgrade");
  const startDateTime = `${calDate.value}T${calStartTime.value}:00`;
  const endDateTime = `${calDate.value}T${calEndTime.value}:00`;
  const location = encodeURIComponent(calLocation.value);
  const body = encodeURIComponent("Water meter replacement appointment. Please clear the space in front of your meter.");
  const timezone = encodeURIComponent(reminderTimezone.value);

  // We intentionally do not append Z because DB time is treated as local slot time.
  return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startDateTime}&enddt=${endDateTime}&timeZone=${timezone}&location=${location}&body=${body}`;
}

function createICSFile() {
  const startDateTime = `${calDate.value}T${calStartTime.value}:00`;
  const endDateTime = `${calDate.value}T${calEndTime.value}:00`;
  const timezone = reminderTimezone.value;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Your Company//Your App//EN',
    `X-WR-TIMEZONE:${timezone}`,
    'BEGIN:VEVENT',
    `DTSTART;TZID=${timezone}:${startDateTime.replace(/[-:]/g, '')}`,
    `DTEND;TZID=${timezone}:${endDateTime.replace(/[-:]/g, '')}`,
    'SUMMARY:Water Meter Upgrade',
    `LOCATION:${calLocation.value}`,
    'DESCRIPTION:Water meter replacement appointment. Please clear the space in front of your meter.',
    `UID:${Date.now()}@yourcompany.com`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
}

// Обробники кнопок
function addToGoogleCalendar() {
  const url = createGoogleCalendarUrl();
  window.open(url, '_blank');
  isModalOpen.value = false;
}

function addToOutlookCalendar() {
  const url = createOutlookCalendarUrl();
  window.open(url, '_blank');
  isModalOpen.value = false;
}

function addToAppleCalendar() {
  //For Apple Calendar, create and upload an .ics file
  const icsContent = createICSFile();
  const blob = new Blob([icsContent], {type: 'text/calendar;charset=utf-8'});
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'water-meter-appointment.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  isModalOpen.value = false;
}

function reloadPage() {
  location.reload();
}
</script>
